// Site tracking, in one place.
//
// Every event is pushed into the Google Tag Manager data layer; GA4 itself is
// configured inside the container rather than on the page, so tags can be added
// or changed without a website release.
//
// Two rules the whole site follows:
//
// 1. An event fires on a verified completion, never on a click that merely
//    intends one. A booking button being pressed is `booking_link_click`;
//    `discovery_call_booked` only ever means Calendly confirmed a booking.
// 2. No personal data is sent. `lib/dataLayer.js` drops any parameter that looks
//    like somebody's details, whatever a caller passes.
//
// While the move to Google's recommended event names settles in, the old name is
// sent alongside the new one so existing GA4 reports don't go blank. Pass the old
// name as the third argument; drop it once the new reports are established.
import { pushToDataLayer, gaClientId, tagManagerLoaded } from "./dataLayer";
import { consentChoice } from "../components/CookieConsent";
import { API } from "./api";

export function trackEvent(name, params = {}, legacyName = "") {
  pushToDataLayer(name, params);
  // Deliberately no gtag('event') mirror here. gtag pushes into the same array
  // Tag Manager reads, and GTM turns a gtag 'event' command into an event of its
  // own — so mirroring registered every action twice in Tag Assistant and fired
  // each GA4 tag twice. GA4 lives in the container; one push is one event.
  if (legacyName) pushToDataLayer(legacyName, params);
}

export { gaClientId };

// ---------------------------------------------------------------------------
// Pricing tool funnel
//
// Six stages, so a drop-out can be seen for what it is: opened the tool, started
// answering, moved between sections, finished the questions, saw a price, went on
// to an enquiry or a call. Only standardised, non-personal values are passed —
// section names, counts, how many services were picked, the quote status. Never
// an answer, a name or a business.
// ---------------------------------------------------------------------------
export const pricing = {
  opened: (params) => trackEvent("pricing_tool_opened", params),
  started: (params) => trackEvent("pricing_tool_started", params, "package_builder_started"),
  step: (params) => trackEvent("pricing_tool_step", params),
  completed: (params) => trackEvent("pricing_tool_completed", params),
  quote: (params) => trackEvent("quote_generated", params, "package_quote_shown"),
  abandoned: (params) => trackEvent("pricing_tool_abandoned", params),
};

// ---------------------------------------------------------------------------
// Delegated listeners: one each for phone, email and podcast links, so links
// added to any page in future are covered without touching this file again.
// ---------------------------------------------------------------------------
const PODCAST_HOSTS = /spotify\.com|apple\.com\/.*podcast|podcasts\.apple|youtube\.com|youtu\.be|amazon\.co\.uk\/.*podcast|pocketcasts|castbox|deezer\.com|podbean|buzzsprout|captivate\.fm|anchor\.fm/i;

let started = false;

// Calendly's own frames, and nothing else. Any page can post a message into a
// browser tab, so without this check a conversion could simply be faked.
function fromCalendly(origin) {
  try {
    const host = new URL(origin).hostname;
    return host === "calendly.com" || host.endsWith(".calendly.com");
  } catch {
    return false;
  }
}

// Reporting a confirmed booking, with the server deciding who owns it.
//
// The browser is the primary route because it is the only one that knows the
// visitor's GA4 session, and therefore their campaign. It does not simply
// announce the booking: it asks the server to claim it against the invitee uri,
// and pushes only if told it owns the report. The webhook then sees the claim and
// stays quiet, so one booking is one conversion.
//
// A claim is provisional. Delivery is confirmed only by Tag Manager's own
// callback that it processed the event and dispatched its tags; if that never
// arrives the claim is released and the server fallback recovers the conversion.
// That callback does not prove Google received the hit — no browser can know
// that — but it does separate "a tag actually ran" from "we pushed into an array".
async function reportConfirmedBooking(data) {
  const invitee_uri = data?.payload?.invitee?.uri || "";
  const event_uri = data?.payload?.event?.uri || "";
  if (!invitee_uri) return;
  const id = window.__cassBookingIntent || "";
  window.__cassBookingIntent = "";
  // Identifies this claimant specifically. Every claim arrives as "browser", so
  // without it a second tab or a replayed message would be waved through as
  // "already yours" and push a second conversion.
  const nonce = `${id || "anon"}-${Math.random().toString(36).slice(2, 10)}`;

  // No container, or no analytics consent: nothing to claim and nothing to push.
  // Where consent is absent the booking is still recorded internally, but GA4 is
  // told nothing by either route.
  if (!tagManagerLoaded() || consentChoice() !== "granted") return;

  let owns = false;
  try {
    const res = await fetch(`${API}/booking/claim`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id, invitee_uri, event_uri, analytics_consent: "granted",
                             nonce, ga_client_id: gaClientId() }),
    });
    owns = res.ok && Boolean((await res.json())?.report);
  } catch {
    // Offline, blocked, server error: stay silent rather than push anyway. The
    // fallback reports it, and one route reporting is the whole point.
    return;
  }
  if (!owns) return;

  const settle = (delivered, reason) => {
    navigator.sendBeacon?.(`${API}/booking/delivered`,
      new Blob([JSON.stringify({ id, invitee_uri, nonce, delivered, reason })],
               { type: "application/json" }));
  };

  let settled = false;
  const pushed = pushToDataLayer("discovery_call_booked",
    { method: "calendly", reporting_route: "browser", page_path: window.location.pathname },
    {
      timeoutMs: 3000,
      onProcessed: () => {
        if (settled) return;
        settled = true;
        settle(true);
      },
    });
  if (!pushed) return;
  // Belt and braces: if the container never calls back at all, release the claim
  // so the conversion is recovered rather than quietly lost.
  setTimeout(() => {
    if (settled) return;
    settled = true;
    settle(false, "Tag Manager did not acknowledge the event within 4 seconds.");
  }, 4000);
}

export function initConversionTracking() {
  if (started || typeof window === "undefined") return;
  started = true;

  document.addEventListener("click", (e) => {
    const link = e.target?.closest?.("a[href]");
    if (!link) return;
    // Admin screens list client phone numbers and addresses — staff admin, not
    // visitor engagement.
    if (window.location.pathname.startsWith("/admin")) return;
    const href = link.getAttribute("href") || "";
    const page_path = window.location.pathname;
    if (href.startsWith("tel:")) trackEvent("phone_click", { page_path }, "phone_call_click");
    else if (href.startsWith("mailto:")) trackEvent("email_click", { page_path }, "contact_email_click");
    else if (PODCAST_HOSTS.test(href)) {
      trackEvent("podcast_click", { page_path, platform: (href.match(/\/\/(?:www\.)?([^/]+)/) || [, ""])[1] });
    }
  }, true);

  window.addEventListener("message", (e) => {
    // Calendly's own frames only, and the scheduled event specifically — not the
    // widget opening, not a date being picked, not the link being clicked.
    if (!fromCalendly(e.origin)) return;
    if (e?.data?.event !== "calendly.event_scheduled") return;
    reportConfirmedBooking(e.data);
  });
}
